<template>
  <div :class="$style.table__box">
    <div v-if="title || exportButton" :class="[$style.table__header, !title ? $style.end : '']">
      <h2 v-if="title" :class="$style.table__title">
        {{ title }}
      </h2>
      
      <button
        v-if="exportButton && rows.length > 0" 
        :class="$style.table__btnExport" 
        @click="exportToCSV"
      >
        <DocumentArrowDownIcon :class="$style.table__btnExportIcon" />
        Export to CSV
      </button>
    </div>

    <template v-if="$slots.filters">
      <slot name="filters" />
    </template>

    <div v-if="showLoadingMessage" :class="$style.table__loading">
      Searching for data...
    </div>
    <template v-else>
      <div v-if="rows.length > 0" :class="$style.table__scroll">

        <table :class="$style.table">
          <thead>
            <tr>
              <td
                v-for="(header, index) in headers"
                :key="index"
                :class="[index % 2 === 0 ? $style.table__secondaryBg : '']"
              >
                <div :class="$style.table__theadContent">
                  <component v-if="header.icon" :is="header.icon" :class="$style.table__theadIcon" />
                  {{ header.title }}
                </div>
              </td>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, rowIndex) in paginatedRows" :key="rowIndex">
              <td
                v-for="(header, colIndex) in headers"
                :key="colIndex"
                :class="[colIndex % 2 === 0 ? $style.table__secondaryBg : '']"
              >
                {{ row[header.key] }}
              </td>
            </tr>
          </tbody>
        </table>
        <div v-if="enablePagination && totalPages > 1" :class="$style.table__pagination">
          <button @click="changePage(currentPage - 1)" :disabled="currentPage === 1">«</button>

          <button 
            v-for="page in paginationRange" 
            :key="page" 
            @click="changePage(page)" 
            :class="{ [$style.active]: page === currentPage }"
          >
            {{ page }}
          </button>

          <span v-if="paginationRange[paginationRange.length - 1] < totalPages">...</span>

          <button 
            v-if="paginationRange[paginationRange.length - 1] < totalPages" 
            @click="changePage(totalPages)"
          >
            {{ totalPages }}
          </button>

          <button @click="changePage(currentPage + 1)" :disabled="currentPage === totalPages">»</button>
        </div>

      </div>
      <template v-else>
        <div :class="$style.table__noData">
          No Data Found
        </div>
      </template>
    </template>

  </div>
</template>


<script setup>
  import { computed } from 'vue';
  import { DocumentArrowDownIcon } from '@heroicons/vue/24/outline';
  import { ref, watch } from 'vue';

  const props = defineProps({
    title: String,
    loadingData: {
      type: Boolean,
      default: true
    },
    exportButton: {
      type: Boolean,
      default: false
    },
    headers: Array,
    rows: Array,
    enablePagination: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    maxVisiblePages: {
      type: Number,
      default: 5
    }
  });

  const showLoadingMessage = computed(() => {
    return props.loadingData;
  });
  const currentPage = ref(1);

  const totalPages = computed(() => {
    return Math.ceil(props.rows.length / props.itemsPerPage);
  });

  const paginatedRows = computed(() => {
    if (!props.enablePagination) return props.rows;

    const start = (currentPage.value - 1) * props.itemsPerPage;
    const end = start + props.itemsPerPage;
    return props.rows.slice(start, end);
  });

  const paginationRange = computed(() => {
    const total = totalPages.value;
    const max = props.maxVisiblePages;
    const pages = [];

    let start = Math.max(currentPage.value - Math.floor(max / 2), 1);
    let end = start + max - 1;

    if (end > total) {
      end = total;
      start = Math.max(1, end - max + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    return pages;
  });

  const changePage = (page) => {
    if (page >= 1 && page <= totalPages.value) {
      currentPage.value = page;
    }
  };

  watch(() => props.rows, () => {
    currentPage.value = 1;
  });

  const exportToCSV = () => {
    if (!props.rows.length) return;

    const csvHeaders = props.headers.map(h => h.title).join(',');

    const csvRows = props.rows.map(row =>
      props.headers.map(h => {
        const val = row[h.key];
        return `"${String(val).replace(/"/g, '""')}"`
      }).join(',')
    );

    const csvContent = [csvHeaders, ...csvRows].join('\n');

    const fileName = `${props.title?.toLowerCase().replace(/\s+/g, '_') || 'export'}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
</script>

<style module lang="scss">

  .table {
    width: 100%;
    color: #C1C1C1;
    margin-bottom: 10px;
    border-radius: 16px;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 22px;
    font-family: $font-secondary;
    border-collapse: collapse;

    &__box {
      gap: 10px;
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;

      &.end {
        justify-content: end !important;
      }
    }

    &__title {
      color: #D9D9D9;
      font-size: 26px;
      font-weight: 600;
      line-height: 36px;

      @include respond-to(null, 'xs') {
        font-size: 22px !important;
      }
    }

    &__btnExport {
      gap: 10px;
      color: #C1C1C1;
      background: rgba(124, 249, 92, 0.15);
      border: 1px solid rgba(124, 249, 92, 0.25);
      border-radius: 8px;
      cursor: pointer;
      display: flex;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 10px;
      padding: 10px 21px;
      align-items: center;
      font-family: $font-secondary;

      @include respond-to(null, 'xs') {
        line-height: 18px !important;

        svg {
          display: none;
        }
      }

      &Icon {
        width: 20px;
        color: #BBF90A;
      }

      &:hover {
        background: #BBF90A;
        color: #232323;

        .table__btnExportIcon {
          color: #232323;
        }
      }
    }

    &__noData {
      font-size: 18px;
      margin-top: 25px;
      font-weight: 800;
      text-align: center;
    }

    th,
    td {
      text-align: left;
      padding: 10px 20px;
      background-color: rgba(45, 45, 45, 0.35);
      border-bottom: 1px solid rgba(83, 83, 83, 0.25);
    }

    &__secondaryBg {
      background-color: rgba(217, 217, 217, 0.05) !important;
    }

    thead {
      td:first-child {
        border-radius: 16px 0 0 0;
      }

      td:last-child {
        border-radius: 0 16px 0 0;
      }
    }

    &__theadContent {
      gap: 8px;
      display: flex;
      align-items: center;
      white-space: nowrap;
    }

    &__theadIcon {
      width: 20px;
      height: 20px;
      color: #C1C1C1;
    }

    tbody {
      td{
        white-space: nowrap;
      }

      tr:last-child {
        td {
          border-bottom: unset;
        }
        td:first-child {
          border-radius: 0 0 0 16px;
        }
        td:last-child {
          border-radius: 0 0 16px 0;
        }
      }
    }

    &__loading {
      font-size: 18px;
      font-weight: 500;
      color: #C1C1C1;
      text-align: center;
      margin-top: 20px;
    }

    &__pagination {
      gap: 10px;
      display: flex;
      margin-top: 10px;
      justify-content: center;
      align-items: center;

      button {
        border: none;
        padding: 8px 14px;
        color: #BBF90A;
        cursor: pointer;
        font-weight: 500;
        border-radius: 4px;
        background: transparent;

        &.active,
        &:hover {
          color: #232323;
          background: #BBF90A;
        }

        &:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    &__scroll {
      overflow-x: scroll;
      padding-bottom: 15px;
    }

  }

</style>